import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatAmount } from 'core/hooks/useFormatAmount'
import { useGetCurrencySymbol } from 'core/hooks/useGetCurrencySymbol'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import AccountCard from './account-card'
import { Outstore } from '../../../../../core/api/trajectory-photo/useApiGetStoreForecast'
import { VpDevicesIcon } from '@vtmn-play/icons/react'
import { useFormatNumber } from '../../../../../core/hooks/useFormatNumber'
import { VpDivider } from '@vtmn-play/react'
import { formatMarginRate } from './format-margin-rate'
import { InputType } from './input-type'

type ChannelOutstoreProps = {
  outstore: Outstore
}

const ChannelOutstore = ({ outstore }: ChannelOutstoreProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const formatAmount = useFormatAmount()
  const getCurrencySymbol = useGetCurrencySymbol()

  const {
    store,
    referenceYear,
    forecastOutstoreQuantities: forecastQuantities,
    setForecastOutstoreGmv,
    isForecastOutstoreGmvValid,
    setForecastOutstoreMarginRate,
    isForecastOutstoreMarginRateValid,
    setForecastOutstoreSellingPrice,
    isForecastOutstoreSellingPriceValid
  } = useContext(StoreTrajectoryContext)

  const currency = store?.currency ?? ''
  const currencySymbol = getCurrencySymbol(currency)

  const referenceQuantities = outstore.reference_quantities
  const forecastQuantitiesProgression = forecastQuantities / referenceQuantities - 1

  return (
    <div className="mg-store-trajectory-edit-channels-outstore">
      <div className="mg-channels-channel-title">
        <VpDevicesIcon size={24} />
        <div className="mg-channels-channel-title-text vp-subtitle-l" data-testid="mg-channels-channel-title-text">
          {t('general.trajectory.store.edit.channels.outstore.title')}
        </div>
      </div>

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.gmv.title', { referenceYear })}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.gmv.description', {
            currency: currencySymbol
          }),
          type: InputType.AMOUNT,
          isValid: isForecastOutstoreGmvValid
        }}
        referenceValue={outstore.reference_gmv}
        referenceProgression={outstore.reference_gmv_progression}
        initialForecastValue={outstore.target_gmv}
        initialForecastProgression={outstore.target_gmv_progression}
        formatValue={value =>
          formatAmount(value, currency, '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        }
        onForecastChange={setForecastOutstoreGmv}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.margin_rate.title', { referenceYear })}
        referenceValue={outstore.reference_margin_rate}
        referenceProgression={outstore.reference_margin_rate_progression}
        initialForecastValue={outstore.target_margin_rate}
        initialForecastProgression={outstore.target_margin_rate_progression}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.margin_rate.description'),
          type: InputType.PERCENTAGE_PRECISE,
          isValid: isForecastOutstoreMarginRateValid
        }}
        formatValue={value => formatMarginRate(formatNumber, value)}
        onForecastChange={setForecastOutstoreMarginRate}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.selling_price.title', { referenceYear })}
        referenceValue={outstore.reference_selling_price}
        referenceProgression={outstore.reference_selling_price_progression}
        initialForecastValue={outstore.target_selling_price}
        initialForecastProgression={outstore.target_selling_price_progression}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.selling_price.description', {
            currency: currencySymbol
          }),
          type: InputType.AMOUNT_PRECISE,
          isValid: isForecastOutstoreSellingPriceValid
        }}
        formatValue={value =>
          formatAmount(value, currency, '--', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
        onForecastChange={setForecastOutstoreSellingPrice}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.quantities', { referenceYear })}
        referenceValue={forecastQuantities}
        referenceProgression={forecastQuantitiesProgression}
        formatValue={value => formatAmount(value, '', '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      />
    </div>
  )
}

export default ChannelOutstore
